import { Button } from '@chakra-ui/button';
import { Center, Flex, Heading, Link, Text } from '@chakra-ui/layout';
import {
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Tooltip,
    useDisclosure,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { FaQuestionCircle } from 'react-icons/fa';

const braveSupportUrl =
    'https://support.brave.com/hc/en-us/articles/360023646212-How-do-I-configure-global-and-site-specific-Shields-settings-';
const firefoxDocsUrl = 'https://developer.mozilla.org/en-US/docs/Web/Privacy/State_Partitioning';
const firefoxConfigUrl = 'about:config';

const ModalLink: React.FC<React.HTMLProps<HTMLAnchorElement>> = ({ href, children }) => (
    <a
        style={{
            color: 'blue',
        }}
        href={href}
        target="_blank"
        rel="noreferrer"
    >
        {children}
    </a>
);

export const Welcome: React.FC<{}> = () => {
    const { keycloak, initialized } = useKeycloak();
    const { isOpen: isBraveOpen, onOpen: onBraveOpen, onClose: onBraveClose } = useDisclosure();
    const {
        isOpen: isFirefoxOpen,
        onOpen: onFirefoxOpen,
        onClose: onFirefoxClose,
    } = useDisclosure();
    const [tooltipShown, setTooltipShown] = useState(true);

    useEffect(() => {
        setTimeout(() => {
            setTooltipShown(false);
        }, 10000);
    });

    return (
        <Center as={Flex} flexDirection="column" height="100%" width="100%">
            <Heading padding={1}>Welcome to STREAMSIX Developer Portal</Heading>
            <Text padding={1}>Your one-stop shop for managing your STREAMSIX-powered games.</Text>
            <Link padding={1} href="#" onClick={() => initialized && keycloak.login()}>
                <Button>Login</Button>
            </Link>
            <Tooltip label="Notice if you use Brave browser" isOpen={tooltipShown}>
                <IconButton
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                    }}
                    aria-label="Help"
                    onClick={() => {
                        onBraveOpen();
                        setTooltipShown(false);
                    }}
                    icon={<FaQuestionCircle />}
                />
            </Tooltip>
            <Modal isOpen={isBraveOpen} onClose={onBraveClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Notice for Brave Browser</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <p>
                            At this time, the <ModalLink href="https://brave.com/">Brave</ModalLink>{' '}
                            browser's Shields feature, by default, will prevent you from staying
                            logged in to the Developer Portal.
                        </p>
                        <p style={{ margin: '1em 0' }}>
                            To fix this issue, you will either need to turn off the Shields feature
                            for this website, or use a different browser.
                        </p>
                        <p>
                            For more information on how to configure Shields settings for a specific
                            site, please see the following page on Brave's website:
                        </p>
                        <ModalLink href={braveSupportUrl}>{braveSupportUrl}</ModalLink>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onBraveClose}>Ok</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Tooltip label="Notice if you use Firefox browser" isOpen={tooltipShown}>
                <IconButton
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                    }}
                    aria-label="Help"
                    onClick={() => {
                        onFirefoxOpen();
                        setTooltipShown(false);
                    }}
                    icon={<FaQuestionCircle />}
                />
            </Tooltip>
            <Modal isOpen={isFirefoxOpen} onClose={onFirefoxClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Notice for Firefox Browser</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <p>
                            Currently the{' '}
                            <ModalLink href="https://www.mozilla.org/en-CA/">Firefox</ModalLink>{' '}
                            browser's cookie partitioning feature, by default, will prevent you from
                            staying logged in to the Developer Portal.
                        </p>
                        <p style={{ margin: '1em 0' }}>
                            To fix this issue, you can change Firefox's cookie configuration
                            settings, or use a different browser.
                        </p>
                        <p>
                            To find out more information, see this{' '}
                            <ModalLink href={firefoxDocsUrl} target="_blank">
                                article
                            </ModalLink>{' '}
                            from the Mozilla Developer's Network Documentation site. To change your
                            cookie configuration settings, open a new tab, and in the search bar
                            enter 'about:config' then hit search. In a separate tab, open the
                            previously listed article and follow the instructions under 'Disable
                            Dynamic State Partitioning'.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onFirefoxClose}>Ok</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Center>
    );
};
